import { createParser } from 'nuqs/server';
import { z } from 'zod';

import { dataTableConfig } from '@app/config/data-table';
import {
  ExtendedColumnFilter,
  ExtendedColumnSort,
} from '@app/types/data-table';

const sortingItemSchema = z.object({
  id: z.string(),
  desc: z.boolean(),
});

export const getSortingStateParser = <TData>(
  columnIds?: string[] | Set<string>
) => {
  const validKeys = columnIds
    ? columnIds instanceof Set
      ? columnIds
      : new Set(columnIds)
    : null;

  return createParser({
    parse: (value) => {
      try {
        const parsed = JSON.parse(value);
        const result = z.array(sortingItemSchema).safeParse(parsed);

        if (!result.success) return null;

        if (validKeys && result.data.some((item) => !validKeys.has(item.id))) {
          return null;
        }

        return result.data as ExtendedColumnSort<TData>[];
      } catch {
        return null;
      }
    },
    serialize: (value) => JSON.stringify(value),
    eq: (a, b) =>
      a.length === b.length &&
      a.every(
        (item, index) =>
          item.id === b[index]?.id && item.desc === b[index]?.desc
      ),
  });
};

const filterItemSchema = z.object({
  id: z.string(),
  value: z.union([z.string(), z.array(z.string())]),
  variant: z.enum(dataTableConfig.filterVariants),
  operator: z.enum(dataTableConfig.operators),
  filterId: z.string(),
});

export type FilterItemSchema = z.infer<typeof filterItemSchema>;

export const getFiltersStateParser = <TData>(
  columnIds?: string[] | Set<string>
) => {
  const validKeys = columnIds
    ? columnIds instanceof Set
      ? columnIds
      : new Set(columnIds)
    : null;

  return createParser({
    parse: (value) => {
      try {
        const parsed = JSON.parse(value);
        const result = z.array(filterItemSchema).safeParse(parsed);

        if (!result.success) return null;

        if (validKeys && result.data.some((item) => !validKeys.has(item.id))) {
          return null;
        }

        return result.data as ExtendedColumnFilter<TData>[];
      } catch {
        return null;
      }
    },
    serialize: (value) => JSON.stringify(value),
    eq: (a, b) =>
      a.length === b.length &&
      a.every(
        (filter, index) =>
          filter.id === b[index]?.id &&
          filter.value === b[index]?.value &&
          filter.variant === b[index]?.variant &&
          filter.operator === b[index]?.operator
      ),
  });
};

/**
 * Sorting object interface for converting to endpoint format
 */
export interface SortingObject {
  id: string;
  desc: boolean;
}

/**
 * Converts a sorting object to the endpoint expected format.
 *
 * @param sortingObject - Object with id (field name) and desc (boolean for descending)
 * @returns String in format "fieldName" or "-fieldName" for descending
 *
 * @example
 * ```typescript
 * convertSortingToEndpointFormat({ id: "name", desc: false }) // returns "name"
 * convertSortingToEndpointFormat({ id: "name", desc: true })  // returns "-name"
 * ```
 */
export function formatSortingForEndpoint(sortingObject: SortingObject): string {
  // Input validation for robustness
  if (!sortingObject || typeof sortingObject !== 'object') {
    throw new Error('Invalid sorting object: must be a non-null object');
  }

  if (!sortingObject.id || typeof sortingObject.id !== 'string') {
    throw new Error('Invalid sorting object: id must be a non-empty string');
  }

  if (typeof sortingObject.desc !== 'boolean') {
    throw new Error('Invalid sorting object: desc must be a boolean');
  }

  // Trim whitespace from id for additional robustness
  const fieldName = sortingObject.id.trim();

  if (fieldName.length === 0) {
    throw new Error(
      'Invalid sorting object: id cannot be empty or whitespace only'
    );
  }

  // Return formatted string - prefix with "-" for descending
  return sortingObject.desc ? `-${fieldName}` : fieldName;
}

/**
 * Converts an array of sorting objects to endpoint format.
 * Useful for multi-column sorting scenarios.
 *
 * @param sortingObjects - Array of sorting objects
 * @returns Array of formatted sorting strings
 *
 * @example
 * ```typescript
 * convertMultipleSortingToEndpointFormat([
 *   { id: "name", desc: false },
 *   { id: "date", desc: true }
 * ]) // returns ["name", "-date"]
 * ```
 */
export function formatMultipleSortingForEndpoint(
  sortingObjects: SortingObject[]
): string[] {
  if (!Array.isArray(sortingObjects)) {
    throw new Error('Invalid input: must be an array of sorting objects');
  }

  return sortingObjects.map(formatSortingForEndpoint);
}

/**
 * Filter object interface matching the expected structure
 */
export interface FilterObject {
  id: string;
  value: string | string[];
  variant: string;
  operator: string;
  filterId: string;
}

/**
 * Finds a filter object by its id from a list of filters.
 *
 * @param filters - Array of filter objects
 * @param id - The id to search for
 * @returns The filter object with the matching id, or undefined if not found
 *
 * @example
 * ```typescript
 * const filters = [
 *   { id: "missingWeight", value: "", variant: "boolean", operator: "eq", filterId: "lcgh7nBy" },
 *   { id: "name", value: "test", variant: "text", operator: "iLike", filterId: "abc123" }
 * ];
 *
 * findFilterById(filters, "missingWeight")
 * // returns { id: "missingWeight", value: "", variant: "boolean", operator: "eq", filterId: "lcgh7nBy" }
 * ```
 */
export function findFilterById(
  filters: FilterObject[],
  id: string
): FilterObject | undefined {
  if (!Array.isArray(filters)) {
    throw new Error('Invalid input: filters must be an array');
  }

  if (!id || typeof id !== 'string') {
    throw new Error('Invalid input: id must be a non-empty string');
  }

  return filters.find((filter) => filter.id === id) || undefined;
}
